import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, Car, Bike, Wrench, Hammer, Rocket, Check, ArrowLeft, Users } from "lucide-react";
import { motion } from "framer-motion";
import { CATEGORIES } from "@/data/categories";
import { toast } from "sonner";

const ICONS = { Building2, Car, Bike, Wrench, Hammer };

export default function ComingSoon() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const category = CATEGORIES.find((c) => c.slug === slug) || CATEGORIES[1];
  const Icon = ICONS[category.icon];
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setJoined(false);
    api.get(`/waitlist/count?category=${slug}`).then((r) => setCount(r.data.count)).catch(() => {});
  }, [slug]);

  const join = async () => {
    if (!email.trim()) { toast.error("Enter your email"); return; }
    setLoading(true);
    try {
      const res = await api.post("/waitlist", { category: slug, email });
      setCount(res.data.waitlist_count);
      setJoined(true);
      toast.success("You're on the list! We'll notify you at launch.");
      setEmail("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join waitlist");
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-[80vh] bg-navy-dark overflow-hidden flex items-center">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #D4AF37 0, transparent 40%), radial-gradient(circle at 80% 60%, #ffffff 0, transparent 35%)" }} />
      <div className="relative max-w-2xl mx-auto px-6 sm:px-8 py-16 text-center">
        <button onClick={() => navigate("/")} data-testid="coming-back-btn" className="inline-flex items-center gap-1.5 text-sm text-blue-100/70 hover:text-white mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to Property
        </button>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
          <div className="mx-auto h-20 w-20 rounded-3xl bg-gold flex items-center justify-center">
            <Icon className="h-10 w-10 text-navy" />
          </div>
          <span className="mt-6 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-gold text-xs font-semibold uppercase tracking-[0.15em]">
            <Rocket className="h-3.5 w-3.5" /> Launching soon
          </span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-white" data-testid="coming-title">
            VYAPAAR <span className="text-gold">{category.label}</span>
          </h1>
          <p className="mt-4 text-lg text-blue-100/80 max-w-md mx-auto">{category.tagline}</p>
          <p className="mt-2 text-sm text-blue-100/60">Same verified, spam-free experience you trust for property — coming to {category.label.toLowerCase()}.</p>
        </motion.div>

        <div className="mt-8 max-w-md mx-auto">
          {joined ? (
            <div className="flex items-center justify-center gap-2 bg-white/10 border border-white/15 rounded-full py-3.5 px-6 text-white" data-testid="waitlist-joined">
              <Check className="h-5 w-5 text-gold" /> You're on the waitlist — we'll be in touch!
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-white rounded-full p-1.5 pl-5 shadow-2xl">
              <Input
                data-testid="waitlist-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && join()}
                placeholder="Enter your email for early access"
                className="flex-1 border-0 shadow-none focus-visible:ring-0 px-0 text-slate-900"
              />
              <Button data-testid="waitlist-join-btn" onClick={join} disabled={loading} className="rounded-full bg-navy hover:bg-navy-dark text-white px-6">
                Notify me
              </Button>
            </div>
          )}
          <p className="mt-4 flex items-center justify-center gap-1.5 text-sm text-blue-100/70" data-testid="waitlist-count">
            <Users className="h-4 w-4 text-gold" /> {count.toLocaleString("en-IN")} already waiting
          </p>
        </div>
      </div>
    </div>
  );
}
